/**
 * /help — index page for the in-app knowledge base.
 *
 * Shows articles grouped by category, filtered to what the current user's
 * role lets them see. Articles themselves render at /help/:slug.
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { articlesByCategory } from '@/help/registry';
import { PageHeader } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/data-display';

export default function HelpIndex() {
  const { user } = useAuth();
  const role = user?.role || 'viewer';

  // useMemo so the registry pass doesn't re-run on every render, even
  // though the dataset is tiny today — it'll grow.
  const groups = useMemo(() => articlesByCategory(role), [role]);

  if (groups.length === 0) {
    return (
      <div className="space-y-6" data-testid="help-index">
        <PageHeader
          title="Help centre"
          description="Topic-by-topic guides to using LinkHub."
        />
        <EmptyState
          icon={BookOpen}
          title="No articles yet"
          description="The knowledge base is being built out. Check back soon."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="help-index">
      <PageHeader
        title="Help centre"
        description="Topic-by-topic guides to using LinkHub. Pick an area to start."
      />

      <div className="space-y-8">
        {groups.map(({ category, items }) => (
          <section key={category} data-testid={`help-category-${category}`}>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((article) => (
                <Link
                  key={article.slug}
                  to={`/help/${article.slug}`}
                  data-testid={`help-article-link-${article.slug}`}
                >
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="p-4 flex items-start gap-3">
                      <BookOpen className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium">{article.title}</h3>
                        {article.summary && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {article.summary}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
